/**
 * Stripe's compliant installments messaging ("as low as $X/mo with Klarna"),
 * rendered above the Payment Element.
 *
 * PROVIDER-RENDERED ON PURPOSE. The per-instalment figure is a lending
 * disclosure, not a price we display: the amount, the term and the APR come
 * from the provider for the buyer's country, and the wording is what keeps the
 * offer compliant. So we hand Stripe the amount + currency + country and render
 * NOTHING of our own around it — never our own copy stating a monthly figure,
 * which would be a second, unverifiable money surface (and would drift the
 * moment a provider changes its terms).
 *
 * The element renders nothing at all when the buyer is not eligible (country,
 * currency or amount out of range), so no empty box or placeholder is needed.
 *
 * The two providers (2026-09-06): Klarna + Afterpay/Clearpay. Affirm is NOT
 * offered and never will be - Affirm rejected the business under its Prohibited
 * Business List, so naming it here would advertise a method no buyer can pick.
 * With a US Stripe account both currently surface for US buyers only (an EUR
 * probe returned card alone).
 *
 * Amount contract: `amountCents` is the CHARGE amount in the CHARGE currency
 * (USD cents for a South-Africa buyer, whose ZAR figures are display-only) —
 * the same amount the PaymentIntent carries. Passing a display amount here
 * would advertise instalments on money that is never charged.
 */
import { PaymentMethodMessagingElement } from "@stripe/react-stripe-js";

/** Countries Stripe accepts for the messaging element's `countryCode`. Anything
 *  else (or an empty value) renders no messaging rather than a broken element.
 *  Kept as-is for Afterpay: its buyer countries (US/CA/GB/AU/NZ) are all already
 *  in here, and Stripe still decides per-country eligibility itself. */
export const SUPPORTED_COUNTRIES = new Set([
  "US", "CA", "GB", "AU", "NZ", "IE",
  "AT", "BE", "DE", "DK", "ES", "FI", "FR", "GR", "IT", "NL", "NO", "PL", "PT", "SE", "CH", "CZ",
]);

export function InstallmentsMessaging({
  enabled,
  amountCents,
  currency,
  countryCode,
}: {
  /** publicConfig.installments_enabled — off means render nothing at all. */
  enabled: boolean;
  /** CHARGE amount in CHARGE-currency minor units (never a display amount). */
  amountCents: number;
  /** ISO currency of the charge, e.g. "USD". */
  currency: string;
  /** Billing country, else the geo country the island already resolved. */
  countryCode: string;
}) {
  const country = (countryCode || "").trim().toUpperCase();
  if (!enabled || amountCents <= 0 || !SUPPORTED_COUNTRIES.has(country)) return null;

  return (
    <div className="mb-3">
      <PaymentMethodMessagingElement
        options={{
          amount: amountCents,
          currency: currency.toUpperCase() as "USD",
          paymentMethodTypes: ["klarna", "afterpay_clearpay"],
          countryCode: country as "US",
        }}
      />
    </div>
  );
}
